<template>
  <div id="user-access-granted">
    <p
      v-if="!hasRoles"
      class="text-muted small"
    >
      Select permissions to see what this user can access.
    </p>

    <div
      v-else
      class="access-granted bg-light border rounded overflow-hidden"
    >
      <div class="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-sm-between bg-light px-3 py-2">
        <button
          id="user-access-granted-toggle"
          type="button"
          class="btn btn-link d-inline-flex align-items-center text-dark font-w600 text-decoration-none p-0 mb-2 mb-sm-0"
          :aria-expanded="expanded ? 'true' : 'false'"
          aria-controls="user-access-granted-list"
          @click="toggleExpanded"
        >
          <i
            class="fa mr-2 font-size-xs"
            :class="expanded ? 'fa-chevron-down' : 'fa-chevron-right'"
          />
          <span>Access granted</span>
          <span
            v-if="areas.length"
            class="text-muted font-w400 font-size-sm ml-2"
          >
            {{ summary }}
          </span>
        </button>

        <b-form-input
          v-if="expanded && areas.length"
          id="user-access-granted-filter"
          v-model="searchQuery"
          type="search"
          size="sm"
          placeholder="Filter by area or action"
          class="access-granted__filter bg-white"
          autocomplete="off"
          @keydown.enter.prevent
        />
      </div>

      <div
        v-if="expanded"
        id="user-access-granted-list"
        class="list-group list-group-flush border-top bg-white access-granted__list"
      >
        <div
          v-if="areas.length === 0"
          class="list-group-item text-muted font-size-sm"
        >
          These permissions do not grant access to any areas.
        </div>

        <template v-else>
          <div
            v-for="area in filteredAreas"
            :key="area.key"
            class="list-group-item access-granted__row"
          >
            <div class="row align-items-start">
              <div class="col-12 col-md-3 text-dark font-w500 mb-1 mb-md-0">
                {{ area.label }}
              </div>
              <div class="col-12 col-md d-flex flex-wrap align-items-center gap-2">
                <span
                  v-for="action in area.actions"
                  :key="action.key"
                  class="rounded badge-dark font-size-sm font-w400 access-granted__chip px-2 py-1"
                >
                  {{ action.label }}
                </span>
              </div>
              <div
                v-if="area.via"
                class="col-12 col-md-auto text-muted font-size-sm mt-1 mt-md-0"
              >
                via {{ area.via }}
              </div>
            </div>
          </div>

          <div
            v-if="filteredAreas.length === 0"
            class="list-group-item text-muted font-size-sm"
          >
            No access matches "{{ searchQuery }}".
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AccessGranted',
  props: {
    roles: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      expanded: true,
      searchQuery: '',
    };
  },
  computed: {
    normalizedRoles() {
      return Array.isArray(this.roles) ? this.roles.filter(Boolean) : [];
    },
    hasRoles() {
      return this.normalizedRoles.length > 0;
    },
    areas() {
      const byKey = {};

      this.normalizedRoles.forEach((role) => {
        const roleLabel = this.formatLabel(role.name);
        const permissions = role.permissions && typeof role.permissions === 'object' ? role.permissions : {};

        Object.keys(permissions).forEach((key) => {
          const rawActions = permissions[key];
          const actions = Array.isArray(rawActions) ? rawActions : [rawActions];
          if (!byKey[key]) {
            byKey[key] = {
              key,
              label: this.formatLabel(key),
              actions: [],
            };
          }

          actions.forEach((action) => {
            if (typeof action !== 'string' && typeof action !== 'number') {
              return;
            }
            const actionKey = String(action);
            if (!actionKey) {
              return;
            }
            let entry = byKey[key].actions.find((item) => item.key === actionKey);
            if (!entry) {
              entry = {
                key: actionKey,
                label: this.formatLabel(actionKey),
                sources: [],
              };
              byKey[key].actions.push(entry);
            }
            if (roleLabel && !entry.sources.includes(roleLabel)) {
              entry.sources.push(roleLabel);
            }
          });
        });
      });

      return Object.values(byKey)
        .filter((area) => area.actions.length > 0)
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    actionCount() {
      return this.areas.reduce((sum, area) => sum + area.actions.length, 0);
    },
    summary() {
      const areaLabel = this.areas.length === 1 ? '1 area' : `${this.areas.length} areas`;
      const actionLabel = this.actionCount === 1 ? '1 action' : `${this.actionCount} actions`;
      return `${areaLabel} · ${actionLabel}`;
    },
    filteredAreas() {
      const query = this.searchQuery.trim().toLowerCase();

      return this.areas.reduce((rows, area) => {
        const nameMatches = !query
          || area.label.toLowerCase().includes(query)
          || area.key.toLowerCase().includes(query);
        const actions = nameMatches
          ? area.actions
          : area.actions.filter((action) => (
            action.label.toLowerCase().includes(query) || action.key.toLowerCase().includes(query)
          ));

        if (!actions.length) {
          return rows;
        }

        const sources = [];
        actions.forEach((action) => {
          action.sources.forEach((source) => {
            if (!sources.includes(source)) {
              sources.push(source);
            }
          });
        });

        rows.push({
          key: area.key,
          label: area.label,
          actions,
          via: sources.join(', '),
        });
        return rows;
      }, []);
    },
  },
  methods: {
    toggleExpanded() {
      this.expanded = !this.expanded;
    },
    formatLabel(value) {
      const humanized = this.$options.filters.humanizedFormat(String(value || ''));
      return this.$options.filters.capitalizeFormat(humanized);
    },
  },
};
</script>

<style scoped>
.access-granted__list {
  max-height: 280px;
  overflow-y: auto;
}

.access-granted__filter {
  width: 100%;
}

@media (min-width: 576px) {
  .access-granted__filter {
    width: 220px;
    margin-left: 1rem;
  }
}
</style>
